import { ExpandMore } from '@mui/icons-material';
import { Accordion, AccordionDetails, AccordionSummary, Grid } from '@mui/material';
import React from 'react';
import { ReactComponent as DBTableIcon } from 'src/new/components/icons/fi-rr-table.svg';
import CDCTablesListWithSearch from './CDCTablesListWithSearch';

const TableMappingAccordion = (props) => {
  const {
    tables,
    criteriaLabel,
    modeLabel,
    isEmpty,
    isPatterns,
    icon,
    resyncTables = [],
    tableFilters = {},
  } = props;

  return (
    <div className="details-accordion-group" aria-label="details-accordion-group-container">
      <Accordion
        className="accordion-item"
        key={criteriaLabel}
        disabled={!tables}
        defaultExpanded={!!tables}
      >
        <AccordionSummary className="accordion-title" expandIcon={<ExpandMore fontSize="large" />}>
          <div className="accordion-title-content">
            <span className="title">{criteriaLabel}</span>
            <div className="toggle-text" />
          </div>
        </AccordionSummary>

        <AccordionDetails className="accordion-body">
          <div className="accordion-body-content-for-cdc">
            <div className="full-width-table">
              <div className="table-header">
                <Grid container className="table-header-row">
                  <Grid item xs={6} gap={1} display="flex" alignItems="center">
                    <span>Mode:</span>
                    <span>{modeLabel}</span>
                  </Grid>
                </Grid>
              </div>
              <div className={'table-body'}>
                {isEmpty && (
                  <Grid container className="table-body-row">
                    <Grid item xs={6}>
                      {isPatterns ? (
                        <span>- No patterns selected</span>
                      ) : (
                        <span>- No tables selected</span>
                      )}
                    </Grid>
                  </Grid>
                )}

                {tables?.map((table, idx) => {
                  if (!table) return null;

                  const isResyncTable = resyncTables.includes(table);
                  const tableFilter = tableFilters[table];
                  const hasTableDetails = isResyncTable || (tableFilter && tableFilter.filter);

                  return (
                    <React.Fragment key={idx}>
                      <Grid container className="table-body-row">
                        <Grid item xs={6} gap={1} display="flex" alignItems="center" gap={1}>
                          {icon}
                          {table}
                        </Grid>
                      </Grid>

                      {/* Indented rows for resync and filter information */}
                      {hasTableDetails && (
                        <>
                          {isResyncTable && (
                            <Grid
                              container
                              className="table-body-row"
                              style={{ paddingLeft: 'var(--spacing-xl)' }}
                            >
                              <Grid item xs={6}>
                                <span style={{ color: '#666', fontSize: '0.9em' }}>
                                  ↳ Re-sync enabled
                                </span>
                              </Grid>
                            </Grid>
                          )}

                          {tableFilter && tableFilter.filter && (
                            <Grid
                              container
                              className="table-body-row"
                              style={{ paddingLeft: 'var(--spacing-xl)' }}
                            >
                              <Grid item xs={6}>
                                <span style={{ color: '#666', fontSize: '0.9em' }}>
                                  ↳ Filter:{' '}
                                  <span style={{ fontFamily: 'monospace' }}>
                                    {tableFilter.filter}
                                  </span>
                                </span>
                              </Grid>
                            </Grid>
                          )}
                        </>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          </div>
        </AccordionDetails>
      </Accordion>
    </div>
  );
};

const getResyncTablesAndFiltersContent = (config) => {
  // Get resync tables configuration
  const resyncTables = config?.['cdc.table.config']?.['resync.tables'] || [];
  const hasResyncTables = resyncTables.length > 0;

  // Get table filters configuration
  const tableFilters = config?.['elt.mode.config'] || {};
  const hasTableFilters = Object.keys(tableFilters).length > 0;

  if (!hasResyncTables && !hasTableFilters) {
    return null;
  }

  return {
    resyncTables,
    tableFilters,
    hasResyncTables,
    hasTableFilters,
  };
};

const getConfigContentForCDCDestination = (config) => {
  if (!config) return null;

  const mapping = config?.mapping || {};
  const hasTablesToExclude = !!mapping?.exclude_tables?.length;
  const label = `(${mapping?.exclude_tables?.length}) Tables to Exclude`;

  return (
    <>
      <div className="detail-row">
        <div className="detail-col">
          <div className="detail-key">Mode</div>
          <div className="detail-value">{mapping?.mode}</div>
        </div>

        <div className="detail-col">
          <div className="detail-key">Replay Row Deletions</div>
          <div className="detail-value">{mapping?.replay_row_deletions ? 'Yes' : 'No'}</div>
        </div>
      </div>

      <div className="detail-row">
        {!!mapping?.table_name_prefix && (
          <div className="detail-col">
            <div className="detail-key">Table Name Prefix</div>
            <div className="detail-value">{mapping?.table_name_prefix}</div>
          </div>
        )}

        {!!mapping?.table_name_suffix && (
          <div className="detail-col">
            <div className="detail-key">Table Name Suffix</div>
            <div className="detail-value">{mapping?.table_name_suffix}</div>
          </div>
        )}
      </div>

      <div className="detail-row">
        {!!mapping?.tracker_mode && (
          <div className="detail-col">
            <div className="detail-key">Tracker Mode</div>
            <div className="detail-value">{mapping?.tracker_mode}</div>
          </div>
        )}

        {!!mapping?.tracker_name && (
          <div className="detail-col">
            <div className="detail-key">Tracker Name</div>
            <div className="detail-value">{mapping?.tracker_name}</div>
          </div>
        )}
      </div>

      <div className="detail-row">
        {!!mapping?.header_template && (
          <div className="detail-col">
            <div className="detail-key">Header Template</div>
            <div className="detail-value">{mapping?.header_template}</div>
          </div>
        )}
      </div>

      {hasTablesToExclude && (
        <TableMappingAccordion
          tables={mapping.exclude_tables}
          criteriaLabel={label}
          modeLabel="Manual Exclude"
          isEmpty={!hasTablesToExclude}
          isPatterns={false}
          icon={<DBTableIcon />}
          disabled={!hasTablesToExclude}
        />
      )}
    </>
  );
};

export const getConfigContentForCDC = (config, isSource) => {
  if (!isSource) return getConfigContentForCDCDestination(config);

  return null;
};

// New function to get CDC tables list component similar to ELT feeds
export const getCDCTablesListContent = (config, isExpanded, onAccordionToggle) => {
  const allTables = config?.['cdc.table.config']?.['include.tables'] || [];

  if (!allTables.length) {
    return null;
  }

  return (
    <CDCTablesListWithSearch
      tables={allTables}
      config={config}
      isExpanded={isExpanded}
      onAccordionToggle={onAccordionToggle}
    />
  );
};

export { getResyncTablesAndFiltersContent };

export const isSqlQueryField = (field) =>
  field?.name?.toLowerCase().includes('sql_query') ||
  field?.name?.toLowerCase() === 'query' ||
  field?.type === 'sql_editor';
